import { useState, useRef } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search, Disc, X } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"

type PrimaryImage = { url: string; alt_text: string }

type RecordingRow = {
  id: number
  title: string
  slug: string
  recordable_type: string
  recordable_name: string | null
  recording_type: string | null
  released_year: number | null
  highest_position: number | null
  primary_image: PrimaryImage | null
}

type Props = {
  recordings: RecordingRow[]
  total_count: number
  filtered_count: number | null
  search: string | null
}

function RecordingCard({ recording }: { recording: RecordingRow }) {
  return (
    <Link
      href={`/recordings/${recording.slug}`}
      className="group block w-full text-left bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {recording.primary_image ? (
          <img
            src={recording.primary_image.url}
            alt={recording.primary_image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
        {recording.highest_position === 1 && (
          <span className="absolute top-2 left-2 text-xs font-bold text-amber-700 dark:text-amber-300 bg-white/90 dark:bg-stone-900/90 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full leading-none">
            #1
          </span>
        )}
      </div>

      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {recording.title}
        </h3>
        {recording.recordable_name && (
          <p
            className="text-xs text-stone-500 dark:text-stone-400 truncate mb-1"
            style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
          >
            {recording.recordable_name}
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          {recording.recording_type && (
            <span className="text-xs px-1.5 py-px rounded bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 leading-none border border-stone-200 dark:border-stone-700">
              {recording.recording_type}
            </span>
          )}
          {recording.released_year && (
            <span
              className="text-xs text-stone-400 dark:text-stone-500 tabular-nums"
              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
            >
              {recording.released_year}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

function statusText(recordings: RecordingRow[], total_count: number, filtered_count: number | null, search: string | null): string {
  if (search) {
    const count = filtered_count ?? recordings.length
    if (recordings.length < count) return `Showing 50 of ${count.toLocaleString()} recordings matching "${search}"`
    return `${count.toLocaleString()} recording${count !== 1 ? "s" : ""} matching "${search}"`
  }
  return `Search to browse all ${total_count.toLocaleString()} recordings`
}

export default function RecordingsIndex({ recordings, total_count, filtered_count, search }: Props) {
  const [query, setQuery] = useState(search ?? "")
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleQueryChange(value: string) {
    setQuery(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      router.get(
        "/recordings",
        value.trim() ? { search: value.trim() } : {},
        { preserveState: true, preserveScroll: true }
      )
    }, 300)
  }

  function clearSearch() {
    setQuery("")
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    router.get("/recordings", {}, { preserveState: true, preserveScroll: true })
  }

  const status = statusText(recordings, total_count, filtered_count, search ?? null)

  return (
    <>
      <Head title="Recordings">
        <meta name="description" content="Browse all Australian music recordings in the Australian Music Database." />
        <meta property="og:title" content="Recordings" />
        <meta property="og:description" content="Browse all Australian music recordings in the Australian Music Database." />
      </Head>

      <PublicShell>
        <div className="mb-8">
          <h1
            className="text-3xl font-bold text-stone-900 dark:text-white mb-1 tracking-tight"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            Recordings
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400">{status}</p>
        </div>

        <div className="relative mb-8 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search recordings…"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 dark:focus:border-amber-500 transition-colors"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {recordings.length === 0 ? (
          <div className="py-20 text-center">
            <Disc className="w-7 h-7 text-stone-300 dark:text-stone-700 mx-auto mb-3" />
            <p className="text-sm text-stone-400 dark:text-stone-500">
              No recordings found for &ldquo;{query}&rdquo;
            </p>
            <button
              onClick={clearSearch}
              className="mt-3 text-sm text-amber-600 dark:text-amber-400 hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
            {recordings.map((recording) => (
              <RecordingCard key={recording.id} recording={recording} />
            ))}
          </div>
        )}
      </PublicShell>
    </>
  )
}
